// src/pages/SystemConfigPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSettings, FiRefreshCw } from 'react-icons/fi';
import styles from './SystemConfigPage.module.css';
import { AdminService, SystemSetting, SystemSettingKey } from '../services/admin.service';
import { SystemService } from '../services/system.service';

type ToggleState = Record<SystemSettingKey, boolean>;

const DEFAULTS: ToggleState = {
  kyc_required: true,
  pld_required: true,
  maintenance_mode: false,
  reports_enabled: true,
  support_enabled: true,
  blogs_enabled: true,
  withdrawals_enabled: true,
  investments_enabled: true,
  developer_requests_enabled: true,
  registrations_enabled: true
};

const SystemConfigPage: React.FC = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<SystemSettingKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [toggles, setToggles] = useState<ToggleState>(DEFAULTS);

  const merged = useMemo(() => {
    const map = new Map<SystemSettingKey, boolean>();
    for (const s of settings) map.set(s.key, !!s.value);

    const next: ToggleState = { ...DEFAULTS };
    (Object.keys(DEFAULTS) as SystemSettingKey[]).forEach(k => {
      if (map.has(k)) next[k] = !!map.get(k);
    });

    return next;
  }, [settings]);

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await AdminService.getSystemSettings();
      const list = Array.isArray(res) ? res : [];
      setSettings(list);

      const nextToggles: ToggleState = { ...DEFAULTS };
      for (const s of list) nextToggles[s.key] = !!s.value;
      setToggles(nextToggles);
    } catch {
      setSettings([]);
      setToggles(DEFAULTS);
      setError(t('admin.system_config_load_error') || 'No se pudo cargar la configuración.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;

    const init = async () => {
      if (!alive) return;
      await load();
    };

    init();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggle = async (key: SystemSettingKey) => {
    const next = !toggles[key];

    setSavingKey(key);
    setError(null);

    const prev = toggles;
    setToggles(s => ({ ...s, [key]: next }));

    try {
      await AdminService.updateSystemSetting({ key, value: next });
      SystemService.invalidate(key);
      await load();
    } catch {
      setToggles(prev);
      setError(t('admin.system_config_save_error') || 'No se pudo guardar el cambio.');
    } finally {
      setSavingKey(null);
    }
  };

  const onRefresh = async () => {
    SystemService.invalidate();
    await load();
  };

  if (loading) return <div>Cargando...</div>;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('admin.system_config_title') || 'Configuración del sistema'}</h1>
          <p className={styles.subtitle}>
            {t('admin.system_config_subtitle') || 'Activa o desactiva módulos y reglas globales.'}
          </p>
        </div>

        <button className={styles.refreshButton} type="button" onClick={onRefresh} disabled={!!savingKey}>
          <FiRefreshCw />
          {t('common.refresh') || 'Actualizar'}
        </button>
      </header>

      {error ? (
        <div className={styles.errorBox}>
          <FiSettings />
          <span>{error}</span>
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>{t('admin.system_config_flags') || 'Flags'}</h2>
        </div>

        <div className={styles.list}>
          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_maintenance') || 'Modo mantenimiento'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_maintenance_desc') || 'Bloquea el portal de clientes y redirige a /mantenimiento.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.maintenance_mode ? styles.on : styles.off}`}
              onClick={() => onToggle('maintenance_mode')}
              disabled={savingKey === 'maintenance_mode'}
              aria-pressed={toggles.maintenance_mode}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_reports') || 'Reportes'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_reports_desc') || 'Habilita/Deshabilita Reportes para clientes.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.reports_enabled ? styles.on : styles.off}`}
              onClick={() => onToggle('reports_enabled')}
              disabled={savingKey === 'reports_enabled'}
              aria-pressed={toggles.reports_enabled}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_blogs') || 'Blog público'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_blogs_desc') || 'Habilita/Deshabilita el acceso a blogs públicos y privados.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.blogs_enabled ? styles.on : styles.off}`}
              onClick={() => onToggle('blogs_enabled')}
              disabled={savingKey === 'blogs_enabled'}
              aria-pressed={toggles.blogs_enabled}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_support') || 'Soporte'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_support_desc') || 'Habilita/Deshabilita el módulo de soporte interno.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.support_enabled ? styles.on : styles.off}`}
              onClick={() => onToggle('support_enabled')}
              disabled={savingKey === 'support_enabled'}
              aria-pressed={toggles.support_enabled}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_withdrawals') || 'Retiros'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_withdrawals_desc') || 'Habilita/Deshabilita retiros para clientes y vista admin de retiros.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.withdrawals_enabled ? styles.on : styles.off}`}
              onClick={() => onToggle('withdrawals_enabled')}
              disabled={savingKey === 'withdrawals_enabled'}
              aria-pressed={toggles.withdrawals_enabled}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_investments') || 'Inversiones'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_investments_desc') || 'Habilita/Deshabilita nuevas inversiones y vistas relacionadas.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.investments_enabled ? styles.on : styles.off}`}
              onClick={() => onToggle('investments_enabled')}
              disabled={savingKey === 'investments_enabled'}
              aria-pressed={toggles.investments_enabled}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_developer_requests') || 'Solicitudes de desarrollador'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_developer_requests_desc') ||
                  'Habilita/Deshabilita la solicitud de acceso como desarrollador para inversionistas.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.developer_requests_enabled ? styles.on : styles.off}`}
              onClick={() => onToggle('developer_requests_enabled')}
              disabled={savingKey === 'developer_requests_enabled'}
              aria-pressed={toggles.developer_requests_enabled}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_registrations') || 'Registro público'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_registrations_desc') || 'Habilita/Deshabilita el acceso a /registro.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.registrations_enabled ? styles.on : styles.off}`}
              onClick={() => onToggle('registrations_enabled')}
              disabled={savingKey === 'registrations_enabled'}
              aria-pressed={toggles.registrations_enabled}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_kyc') || 'KYC requerido'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_kyc_desc') || 'Restringe inversiones si KYC no está aprobado.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.kyc_required ? styles.on : styles.off}`}
              onClick={() => onToggle('kyc_required')}
              disabled={savingKey === 'kyc_required'}
              aria-pressed={toggles.kyc_required}
            >
              <span className={styles.knob} />
            </button>
          </div>

          <div className={styles.item}>
            <div className={styles.itemInfo}>
              <div className={styles.itemTitle}>{t('admin.flag_pld') || 'PLD requerido'}</div>
              <div className={styles.itemDesc}>
                {t('admin.flag_pld_desc') || 'Restringe inversiones si PLD no está aprobado.'}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${toggles.pld_required ? styles.on : styles.off}`}
              onClick={() => onToggle('pld_required')}
              disabled={savingKey === 'pld_required'}
              aria-pressed={toggles.pld_required}
            >
              <span className={styles.knob} />
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SystemConfigPage;
